<template>
    <div
        ref="modalContainer"
        class="vrm-modal vrm-modal--wide vrm-modal--search-chats mfp-with-anim"
    >
        <div class="vrm-modal__header">
            <div class="vrm-modal__search-container">
                <input
                    ref="searchInput"
                    v-model="searchQuery"
                    type="text"
                    class="vrm-modal__search-input sg-element"
                    :placeholder="
                        $t('ai-assistant.search_placeholder', { i18nFallback: 'Search chats...' })
                    "
                    @input="onSearchInput"
                />
            </div>
            <button class="vrm-modal__close" aria-label="Close modal" @click="onClose">
                <span class="sg-vrm-icon_055_cross-16px"></span>
            </button>
        </div>
        <div class="vrm-modal__content">
            <div class="vrm-modal__section">
                <button class="vrm-modal__item vrm-modal__item--new-chat" @click="createNewChat">
                    <span class="vrm-modal__item-text">
                        {{ $t('ai-assistant.start_new_chat', { i18nFallback: 'New chat' }) }}
                    </span>
                </button>
            </div>

            <div v-if="shouldShowChatList" class="vrm-modal__section">
                <div
                    v-for="group in groupedChats"
                    :key="group.category"
                    class="vrm-modal__date-group"
                >
                    <div v-if="!isSearching" class="vrm-modal__section-header">
                        {{ group.displayName }}
                    </div>
                    <div class="vrm-modal__items">
                        <div v-if="!isSearching">
                            <button
                                v-for="chat in group.chats"
                                :key="chat.chatIdentifier"
                                class="vrm-modal__item"
                                @click="selectChat(chat)"
                            >
                                <span class="vrm-modal__item-icon sg-vrm-icon_149_ai-chat"></span>
                                <span class="vrm-modal__item-text">{{ chat.name }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="isSearching" class="vrm-modal__skeleton-container">
                <div v-for="i in 5" :key="`skeleton-${i}`" class="vrm-modal__skeleton-item">
                    <div class="vrm-modal__skeleton-icon"></div>
                    <div class="vrm-modal__skeleton-text">
                        <div class="vrm-modal__skeleton-line"></div>
                    </div>
                </div>
            </div>
            <div
                v-if="searchQuery && !isSearching && filteredChats.length === 0"
                id="no-results"
                class="vrm-modal__no-results"
            >
                <span class="sg-vrm-icon_031_search-16px"></span>
                <p>
                    {{ $t('ai-assistant.no_search_results', { i18nFallback: 'No chats found' }) }}
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import {
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATIONS,
    VrmNotification,
} from '@/vue/store/modules/notifications';

@Component({
    components: {},
})
@AngularLocaleBundle('ai-assistant')
export default class SearchChatsModal extends Vue {
    @Prop({ type: Array, default: () => [] })
    chats;

    @Prop({ type: String, default: null })
    currentChatIdentifier;

    // Search state management
    searchQuery = '';
    debouncedSearchQuery = '';
    isSearching = false;
    searchTimeout = null;

    // Outside click detection state
    mouseDownTarget = null;

    // Debounce delay in milliseconds
    static SEARCH_DEBOUNCE_DELAY = 700;

    mounted() {
        this.$nextTick(() => {
            setTimeout(() => {
                if (this.$refs.searchInput) {
                    this.$refs.searchInput.focus();
                }
            }, 100);
        });

        document.addEventListener('mousedown', this.handleMouseDown);
        document.addEventListener('mouseup', this.handleMouseUp);
    }

    beforeDestroy() {
        this.clearSearchTimeout();
        document.removeEventListener('mousedown', this.handleMouseDown);
        document.removeEventListener('mouseup', this.handleMouseUp);
    }

    /**
     * Handle mousedown events to track where the user started clicking
     */
    handleMouseDown(event) {
        this.mouseDownTarget = event.target;
    }

    /**
     * Handle mouseup events to determine if it's a valid outside click
     */
    handleMouseUp(event) {
        if (
            this.mouseDownTarget === event.target &&
            this.$refs.modalContainer &&
            !this.$refs.modalContainer.contains(event.target)
        ) {
            this.onClose();
        }
        this.mouseDownTarget = null;
    }

    /**
     * Filtered chats based on the debounced search query
     */
    get filteredChats() {
        const query = this.debouncedSearchQuery.trim();
        if (!query) {
            return this.chats;
        }

        const lowerQuery = query.toLowerCase();
        return this.chats.filter(chat => {
            return chat.name.toLowerCase().includes(lowerQuery);
        });
    }

    /**
     * Groups filtered chats by their creation date
     */
    get groupedChats() {
        const groups = new Map();

        this.filteredChats.forEach(chat => {
            const category = this.getChatDateCategory(chat.createdAt);
            const displayName = this.getDateDisplayName(category, chat.createdAt);

            if (!groups.has(category)) {
                groups.set(category, {
                    category,
                    displayName,
                    chats: [],
                    sortOrder: this.getDateSortOrder(category),
                });
            }

            groups.get(category).chats.push(chat);
        });

        return Array.from(groups.values())
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(group => ({
                category: group.category,
                displayName: group.displayName,
                chats: group.chats.sort((a, b) => b.createdAt - a.createdAt),
            }));
    }

    /**
     * Determines when to show the chat list section
     */
    get shouldShowChatList() {
        const hasSearchQuery = !!this.debouncedSearchQuery.trim();
        const hasResults = this.groupedChats.some(group => group.chats.length > 0);
        return !hasSearchQuery || hasResults;
    }

    /**
     * Handles search input with proper debouncing
     */
    onSearchInput() {
        this.clearSearchTimeout();

        // If there's a search query, start the debounced search
        if (this.searchQuery.trim()) {
            this.isSearching = true;

            this.searchTimeout = setTimeout(() => {
                this.debouncedSearchQuery = this.searchQuery;
                this.isSearching = false;
            }, SearchChatsModal.SEARCH_DEBOUNCE_DELAY);
        } else {
            this.debouncedSearchQuery = '';
            this.isSearching = false;
        }
    }

    clearSearchTimeout() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = null;
        }
    }

    /**
     * Categorizes a chat based on its creation
     */
    getChatDateCategory(createdAt) {
        const now = new Date();
        const chatDate = new Date(createdAt * 1000);

        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const chatDateOnly = new Date(
            chatDate.getFullYear(),
            chatDate.getMonth(),
            chatDate.getDate(),
        );

        if (chatDateOnly.getTime() === today.getTime()) {
            return 'today';
        } else if (chatDateOnly.getTime() === yesterday.getTime()) {
            return 'yesterday';
        } else {
            return chatDate.toISOString().split('T')[0];
        }
    }

    /**
     * Gets the display name for a date category
     */
    getDateDisplayName(category, createdAt) {
        switch (category) {
            case 'today':
                return this.$t('ai-assistant.today', { i18nFallback: 'Today' });
            case 'yesterday':
                return this.$t('ai-assistant.yesterday', { i18nFallback: 'Yesterday' });
            default: {
                const date = new Date(createdAt * 1000);
                return date.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                });
            }
        }
    }

    /**
     * Gets the sort order for date categories
     */
    getDateSortOrder(category) {
        switch (category) {
            case 'today':
                return 0;
            case 'yesterday':
                return 1;
            default:
                return (
                    2 +
                    (new Date().getTime() - new Date(category).getTime()) / (1000 * 60 * 60 * 24)
                );
        }
    }

    /**
     * Emits close event to create a new chat
     */
    createNewChat() {
        // Show success notification
        this.$store.dispatch(NOTIFICATIONS.ADD, {
            notification: new VrmNotification({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('ai-assistant.new_chat_created', {
                    i18nFallback: 'Chat created successfully!',
                }),
            }),
        });
        this.$emit('close', { action: 'newChat' });
    }

    /**
     * Emits close event with selected chat data
     */
    selectChat(chat) {
        this.$emit('close', { action: 'selectChat', chat });
    }

    /**
     * Emits close event to close the modal
     */
    onClose() {
        this.$emit('close', { action: 'close' });
    }
}
</script>
